import { useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiEdit2,
  FiGift,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShoppingBag,
  FiUser,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import CustomerModal from "../components/CustomerModal";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const dateOnly = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
      }).format(new Date(value))
    : "—";

const orderStatus = {
  DRAFT: "Draft",
  CONFIRMED: "Confirmed",
  PARTIALLY_DELIVERED: "Partially Delivered",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
};

export default function CustomerDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);

  const loadCustomer = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest(`/customers/${id}`, {
        accessToken,
      });

      setCustomer(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load customer.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && id) {
      loadCustomer();
    }
  }, [accessToken, id]);

  const summary = useMemo(() => {
    const salesOrders = customer?.salesOrders || [];

    return {
      totalOrders:
        Number(customer?._count?.salesOrders || 0) +
        Number(customer?._count?.posSales || 0) +
        Number(customer?._count?.ecommerceOrders || 0),

      salesOrderValue: salesOrders.reduce(
        (total, order) => total + Number(order.totalAmount || 0),
        0,
      ),

      salesOrderCount: salesOrders.length,

      rewardPoints: Number(customer?.loyaltyAccount?.pointsBalance || 0),
    };
  }, [customer]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-700 animate-spin" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
        {error || "Customer not found."}
      </div>
    );
  }

  const primaryAddress = customer.addresses?.[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/customers")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft />
            Back To Customers
          </button>

          <h1 className="text-4xl font-bold">{customer.fullName}</h1>

          <div className="flex flex-wrap gap-3 mt-4">
            <span className="px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">
              ID: {customer.id.slice(0, 8).toUpperCase()}
            </span>

            <StatusBadge status={customer.status} />

            {customer.marketingOptIn && (
              <span className="px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                Marketing Opt-In
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => setShowEditModal(true)}
          className="bg-primary-700 text-white px-6 h-12 rounded-xl flex items-center gap-2"
        >
          <FiEdit2 />
          Edit Customer
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div>
            <div className="flex gap-5">
              <div className="h-24 w-24 rounded-3xl bg-primary-100 flex items-center justify-center text-primary-700 text-4xl font-bold">
                {customer.fullName
                  .split(" ")
                  .map((word) => word[0])
                  .join("")
                  .slice(0, 2)}
              </div>

              <div>
                <h2 className="text-3xl font-bold">{customer.fullName}</h2>
                <p className="text-gray-500 mt-2">Retail Customer</p>
                <p className="text-gray-500">
                  Joined {dateOnly(customer.createdAt)}
                </p>
              </div>
            </div>

            <div className="space-y-4 mt-8">
              <Contact icon={<FiPhone />} value={customer.phone} />
              <Contact icon={<FiMail />} value={customer.email} />
              <Contact
                icon={<FiMapPin />}
                value={formatAddress(primaryAddress)}
              />
            </div>
          </div>

          <div>
            <h3 className="font-bold text-xl mb-6">Customer Information</h3>

            <Info
              label="Status"
              value={customer.status === "ACTIVE" ? "Active" : "Disabled"}
            />
            <Info
              label="Email Verified"
              value={customer.isEmailVerified ? "Yes" : "No"}
            />
            <Info
              label="Phone Verified"
              value={customer.isPhoneVerified ? "Yes" : "No"}
            />
            <Info
              label="Reward Points"
              value={summary.rewardPoints.toLocaleString()}
            />
            <Info
              label="Saved Addresses"
              value={customer.addresses?.length || 0}
            />
          </div>

          <div>
            <div className="bg-primary-700 text-white rounded-3xl p-8">
              <p className="opacity-80">Sales Order Value</p>

              <h2 className="text-4xl font-bold mt-3">
                {money(summary.salesOrderValue)}
              </h2>

              <div className="mt-8 space-y-5">
                <Metric title="Sales Orders" value={summary.salesOrderCount} />
                <Metric title="All Order Records" value={summary.totalOrders} />
                <Metric
                  title="Reward Points"
                  value={summary.rewardPoints.toLocaleString()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-4 gap-6">
        <Stat
          title="All Orders"
          value={summary.totalOrders}
          icon={<FiShoppingBag size={24} />}
          color="blue"
        />

        <Stat
          title="Sales Orders"
          value={summary.salesOrderCount}
          icon={<FiCheckCircle size={24} />}
          color="green"
        />

        <Stat
          title="Sales Order Value"
          value={money(summary.salesOrderValue)}
          icon={<FiUser size={24} />}
          color="purple"
        />

        <Stat
          title="Reward Points"
          value={summary.rewardPoints.toLocaleString()}
          icon={<FiGift size={24} />}
          color="yellow"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-100 flex overflow-x-auto">
          {["overview", "sales orders", "ecommerce orders", "addresses"].map(
            (tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-5 capitalize font-medium whitespace-nowrap ${
                  activeTab === tab
                    ? "border-b-2 border-primary-700 text-primary-700 bg-primary-50"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {tab}
              </button>
            ),
          )}
        </div>

        {activeTab === "overview" && (
          <div className="p-8 grid lg:grid-cols-2 gap-8">
            <div>
              <SectionTitle title="Customer Profile" />
              <Info label="Customer Name" value={customer.fullName} />
              <Info label="Email" value={customer.email} />
              <Info label="Phone" value={customer.phone} />
              <Info
                label="Marketing Consent"
                value={customer.marketingOptIn ? "Granted" : "Not granted"}
              />
            </div>

            <div>
              <SectionTitle title="Primary Address" />
              <Info label="Recipient" value={primaryAddress?.recipient} />
              <Info label="Address" value={formatAddress(primaryAddress)} />
              <Info label="City" value={primaryAddress?.city} />
              <Info
                label="State / Pincode"
                value={[primaryAddress?.state, primaryAddress?.postalCode]
                  .filter(Boolean)
                  .join(" · ")}
              />
            </div>
          </div>
        )}

        {activeTab === "sales orders" && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <Header>Order No</Header>
                  <Header>Date</Header>
                  <Header>Store</Header>
                  <Header align="center">Items</Header>
                  <Header align="right">Amount</Header>
                  <Header align="center">Status</Header>
                  <Header align="center">Action</Header>
                </tr>
              </thead>

              <tbody>
                {customer.salesOrders?.length ? (
                  customer.salesOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-5 font-semibold">
                        {order.salesOrderNumber}
                      </td>

                      <td className="px-6 py-5">{dateOnly(order.orderDate)}</td>

                      <td className="px-6 py-5">{order.store?.name || "—"}</td>

                      <td className="px-6 py-5 text-center">
                        {order._count?.items || 0}
                      </td>

                      <td className="px-6 py-5 text-right font-semibold">
                        {money(order.totalAmount)}
                      </td>

                      <td className="px-6 py-5 text-center">
                        <OrderStatusBadge status={order.status} />
                      </td>

                      <td className="px-6 py-5 text-center">
                        <button
                          onClick={() =>
                            navigate(`/admindashboard/sales/orders/${order.id}`)
                          }
                          className="text-primary-700 font-semibold"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="py-16 text-center text-gray-500">
                      No sales orders created for this customer yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "ecommerce orders" && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <Header>Order No</Header>
                  <Header>Date</Header>
                  <Header align="center">Items</Header>
                  <Header align="center">Payment</Header>
                  <Header align="right">Amount</Header>
                  <Header align="center">Status</Header>
                </tr>
              </thead>

              <tbody>
                {customer.ecommerceOrders?.length ? (
                  customer.ecommerceOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-5 font-semibold">
                        {order.orderNumber}
                      </td>

                      <td className="px-6 py-5">{dateOnly(order.placedAt)}</td>

                      <td className="px-6 py-5 text-center">
                        {order._count?.items || 0}
                      </td>

                      <td className="px-6 py-5 text-center">
                        <div>
                          <p className="font-medium">{order.paymentMethod}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {order.paymentStatus}
                          </p>
                        </div>
                      </td>

                      <td className="px-6 py-5 text-right font-semibold">
                        {money(order.totalAmount)}
                      </td>

                      <td className="px-6 py-5 text-center">
                        <EcommerceStatusBadge status={order.status} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-16 text-center text-gray-500">
                      No ecommerce orders for this customer yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "addresses" && (
          <div className="p-8 grid md:grid-cols-2 gap-6">
            {customer.addresses?.length ? (
              customer.addresses.map((address) => (
                <div
                  key={address.id}
                  className="rounded-3xl border border-gray-100 p-6"
                >
                  <div className="flex justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-lg">
                        {address.label || "Address"}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {address.isDefault
                          ? "Default Address"
                          : "Saved Address"}
                      </p>
                    </div>

                    {address.isDefault && (
                      <span className="h-fit px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                        Default
                      </span>
                    )}
                  </div>

                  <p className="mt-5 font-medium">{address.recipient}</p>
                  <p className="mt-2 text-gray-600 leading-7">
                    {formatAddress(address)}
                  </p>
                  <p className="mt-3 text-sm text-gray-500">
                    {address.phone || "No contact number"}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No saved addresses.</p>
            )}
          </div>
        )}
      </div>

      <CustomerModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        customer={customer}
        onSaved={loadCustomer}
      />
    </div>
  );
}

function formatAddress(address) {
  if (!address) return "No saved address";

  return [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
}

function Contact({ icon, value }) {
  return (
    <div className="flex gap-3">
      <span className="text-primary-700 mt-1">{icon}</span>
      <span>{value || "—"}</span>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex justify-between gap-6 py-4 border-b border-gray-100 last:border-b-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right break-words">
        {value || "—"}
      </span>
    </div>
  );
}

function Metric({ title, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="opacity-80">{title}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function Stat({ title, value, icon, color }) {
  const colors = {
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
    purple: "bg-purple-100 text-purple-700",
    yellow: "bg-yellow-100 text-yellow-700",
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <h2 className="text-2xl font-bold mt-2">{value}</h2>
      </div>

      <div
        className={`h-14 w-14 rounded-2xl flex items-center justify-center ${colors[color]}`}
      >
        {icon}
      </div>
    </div>
  );
}

function SectionTitle({ title }) {
  return <h3 className="font-bold text-xl mb-5">{title}</h3>;
}

function Header({ children, align = "left" }) {
  return (
    <th
      className={`px-6 py-4 text-${align} text-xs font-semibold uppercase text-gray-500`}
    >
      {children}
    </th>
  );
}

function StatusBadge({ status }) {
  const active = status === "ACTIVE";

  return (
    <span
      className={`px-4 py-2 rounded-full text-sm font-semibold ${active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
    >
      {active ? "Active" : "Disabled"}
    </span>
  );
}

function OrderStatusBadge({ status }) {
  const styles = {
    DRAFT: "bg-gray-100 text-gray-700",
    CONFIRMED: "bg-blue-100 text-blue-700",
    PARTIALLY_DELIVERED: "bg-purple-100 text-purple-700",
    DELIVERED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || "bg-gray-100 text-gray-700"}`}
    >
      {orderStatus[status] || status}
    </span>
  );
}

function EcommerceStatusBadge({ status }) {
  const styles = {
    PENDING_PAYMENT: "bg-yellow-100 text-yellow-700",
    CONFIRMED: "bg-blue-100 text-blue-700",
    PROCESSING: "bg-purple-100 text-purple-700",
    PARTIALLY_SHIPPED: "bg-indigo-100 text-indigo-700",
    SHIPPED: "bg-cyan-100 text-cyan-700",
    DELIVERED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
    PAYMENT_FAILED: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        styles[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status.replaceAll("_", " ")}
    </span>
  );
}
